import {useTranslation} from 'react-i18next';
import type {ReactNode} from 'react';
import {Camera, Eye, Hand, ImageIcon, Scan, Sun, Upload, User, X} from 'lucide-react';
import {cn} from '@chekinapp/ui';

export type QuickFixesVariant = 'document' | 'face' | 'liveness' | 'file';

const CAUSE_KEY: Record<QuickFixesVariant, string> = {
  document: 'quick_fix.document_moved',
  face: 'quick_fix.face_moved',
  liveness: 'quick_fix.liveness_not_completed',
  file: 'quick_fix.file_unreadable',
};

const TIP_KEY: Record<QuickFixesVariant, string> = {
  document: 'quick_fix.hold_still',
  face: 'quick_fix.face_lighting',
  liveness: 'quick_fix.liveness_follow_prompts',
  file: 'quick_fix.file_sharp_photo',
};

const ERROR_TIPS: Record<QuickFixesVariant, [ReactNode, string][]> = {
  document: [
    [<Camera key="fit" />, 'error_tip_document_fit'],
    [<Sun key="light" />, 'error_tip_document_light'],
    [<Hand key="still" />, 'error_tip_document_still'],
  ],
  face: [
    [<User key="uncover" />, 'error_tip_face_uncover'],
    [<Sun key="light" />, 'error_tip_face_light'],
    [<Eye key="look" />, 'error_tip_face_look'],
  ],
  liveness: [
    [<User key="uncover" />, 'error_tip_face_uncover'],
    [<Sun key="light" />, 'error_tip_face_light'],
    [<Eye key="prompts" />, 'quick_fix.liveness_follow_prompts'],
  ],
  file: [
    [<ImageIcon key="original" />, 'error_tip_file_original'],
    [<Scan key="corners" />, 'error_tip_file_corners'],
    [<Sun key="sharp" />, 'error_tip_file_sharp'],
  ],
};

export type QuickFixesProps = {
  variant: QuickFixesVariant;
  showUpload?: boolean;
  layout?: 'card' | 'tiles';
  className?: string;
};

const TILE_CLASS =
  'grid size-8 flex-none place-items-center rounded-[10px] bg-[var(--chekin-blue-100)] text-[var(--chekin-color-brand-blue)] [&_svg]:size-[18px]';

export function QuickFixes({
  variant,
  showUpload,
  layout = 'card',
  className,
}: QuickFixesProps) {
  const {t} = useTranslation('identity-verification');

  if (layout === 'tiles') {
    return (
      <ul className={cn('m-0 flex list-none flex-col gap-2 p-0', className)}>
        {ERROR_TIPS[variant].map(([icon, key]) => (
          <li
            key={key}
            className="flex items-center gap-3 text-[14px] font-medium leading-[18px] text-[var(--chekin-text-primary)]"
          >
            <span aria-hidden className={TILE_CLASS}>
              {icon}
            </span>
            {t(key)}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div
      className={cn(
        'w-full max-w-[var(--capture-max-width,480px)] rounded-[14px] border border-[var(--chekin-border-default)] bg-[var(--chekin-color-white)] p-4 text-left',
        className,
      )}
    >
      <p className="m-0 mb-2.5 text-[14px] font-bold text-[var(--chekin-color-brand-navy)]">
        {t('quick_fixes')}
      </p>
      <ul className="m-0 flex list-none flex-col gap-2 p-0 text-[13px] leading-[18px] text-[var(--chekin-color-gray-1)]">
        <li className="flex items-start gap-2.5">
          <X className="mt-px h-4 w-4 flex-none text-[var(--chekin-red-soft)]" />
          {t(CAUSE_KEY[variant])}
        </li>
        <li className="flex items-start gap-2.5">
          <Sun className="mt-px h-4 w-4 flex-none text-[var(--chekin-color-brand-blue)]" />
          {t(TIP_KEY[variant])}
        </li>
        {showUpload && (
          <li className="flex items-start gap-2.5">
            <Upload className="mt-px h-4 w-4 flex-none text-[var(--chekin-color-brand-blue)]" />
            {t('quick_fix.upload_instead')}
          </li>
        )}
      </ul>
    </div>
  );
}
